<template>
  <div class="text-step">
    <el-card class="upload-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span>上传小说原文</span>
          <el-button type="primary" @click="saveText" :loading="saving">
            <el-icon><Check /></el-icon>
            保存
          </el-button>
        </div>
      </template>

      <el-tabs v-model="uploadMode">
        <el-tab-pane label="直接输入" name="input">
          <el-input
            v-model="storyText"
            type="textarea"
            :rows="15"
            placeholder="请在此粘贴小说原文..."
            maxlength="200000"
            show-word-limit
          />
        </el-tab-pane>
        
        <el-tab-pane label="文件上传" name="file">
          <el-upload
            class="upload-area"
            drag
            action="#"
            :auto-upload="false"
            :on-change="handleFileChange"
            accept=".txt,.doc,.docx"
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              拖拽文件到此处或 <em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                支持 .txt, .doc, .docx 格式，文件大小不超过 10MB
              </div>
            </template>
          </el-upload>
        </el-tab-pane>

        <el-tab-pane label="从网盘选择" name="netdisk">
          <div class="netdisk-entry">
            <p class="netdisk-hint">
              从 AIGC 网盘中选择本项目的「0 素材」文件夹内上传的文本文件（如小说 TXT）
            </p>
            <el-button type="primary" plain @click="pickerVisible = true">
              <el-icon><FolderOpened /></el-icon>
              打开网盘选择器
            </el-button>
          </div>

          <NetDiskPicker
            v-model="pickerVisible"
            :project-id="projectId"
            @select="handleNetdiskSelect"
            @select-novel="handleNetdiskNovel"
          />
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 超长小说分段阅读器 -->
    <NovelReader
      v-model="readerVisible"
      :project-id="projectId"
      @select="handleReaderSelect"
    />

    <el-card class="info-card" shadow="never">
      <template #header>
        <span>提示</span>
      </template>
      <ul class="tips-list">
        <li>支持上传 .txt、.doc、.docx 格式的文本文件</li>
        <li>超过 2MB 的超长 TXT 会自动进入分段阅读器，可按章节勾选导入</li>
        <li>文本内容将用于后续的设定提取和剧本拆解</li>
        <li>上传后可以随时修改和重新上传</li>
      </ul>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { useProjectStore } from '../../stores/project'
import { ingestNovelFile } from '../../api/project'
import NetDiskPicker from './NetDiskPicker.vue'
import NovelReader from './NovelReader.vue'

const props = defineProps({
  projectId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['step-complete', 'step-save'])

const projectStore = useProjectStore()
const uploadMode = ref('input')
const storyText = ref('')
const saving = ref(false)
const pickerVisible = ref(false)
const readerVisible = ref(false)
const parsingFile = ref(false)

// 长文阈值：与后端 AMJ_Novel_Parser::SIZE_THRESHOLD 一致（2MB ≈ 50万字）
const NOVEL_SIZE_THRESHOLD = 2 * 1024 * 1024

// 加载已有文本
onMounted(async () => {
  if (projectStore.project?.original_text) {
    storyText.value = projectStore.project.original_text
  }
})

// 处理文件上传
const handleFileChange = async (file) => {
  const raw = file.raw || file

  // 超长文本：不做 FileReader 直读（内存撑爆 textarea），改走服务端分段解析
  if (raw.size > NOVEL_SIZE_THRESHOLD) {
    parsingFile.value = true
    try {
      await ingestNovelFile(props.projectId, raw)
      ElMessage.success('长文已解析，请在阅读器中选择要导入的章节')
      readerVisible.value = true
    } catch (e) {
      console.error('Failed to parse novel file:', e)
      ElMessage.error(e.message || '长文解析失败，请重试')
    } finally {
      parsingFile.value = false
    }
    return
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    storyText.value = e.target.result
    ElMessage.success('文件读取成功')
  }
  reader.readAsText(raw)
}

// 网盘选择回调：回填文本内容（小文件直读模式）
const handleNetdiskSelect = ({ name, text }) => {
  storyText.value = text
  uploadMode.value = 'input'
  ElMessage.success(`已从网盘导入：${name}`)
}

// 网盘选择回调：超大文本已由服务端落盘建索引，打开阅读器选择章节
const handleNetdiskNovel = ({ name }) => {
  ElMessage.success(`已解析网盘长文：${name}`)
  readerVisible.value = true
}

// 阅读器导入回调：拼接后的章节文本回填
const handleReaderSelect = ({ name, text }) => {
  storyText.value = text
  uploadMode.value = 'input'
  ElMessage.success(`已导入：${name}（${text.length} 字）`)
}

// 保存文本
const saveText = async () => {
  if (!storyText.value.trim()) {
    ElMessage.warning('请输入或上传小说原文')
    return
  }

  saving.value = true
  try {
    await projectStore.uploadText(storyText.value)
    ElMessage.success('保存成功')
    emit('step-save')
    emit('step-complete')
  } catch (error) {
    console.error('Failed to save text:', error)
    ElMessage.error('保存失败')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped lang="scss">
.text-step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.upload-card {
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.upload-area {
  :deep(.el-upload) {
    width: 100%;
  }

  :deep(.el-upload-dragger) {
    width: 100%;
    height: 200px;
  }
}

.netdisk-entry {
  text-align: center;
  padding: 30px 0;

  .netdisk-hint {
    margin-bottom: 16px;
    color: #909399;
    font-size: 13px;
  }
}

.tips-list {
  margin: 0;
  padding-left: 20px;
  
  li {
    margin-bottom: 8px;
    color: #606266;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
